import { Box } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  commaFormat,
  getDayOfWeek,
  getEffectiveDaysInMonth,
  getMonthListByMax,
} from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwGenderLegend } from '../commonComponent/SwGenderLegend';

interface Props {
  stepsGoalRateAverage?: boolean[];
  data?: any[];
  startDate: number;
  totalAverage: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwAverageGraphByGender: React.FC<Props> = ({
  stepsGoalRateAverage = [false, false],
  data,
  startDate,
  totalAverage,
  from = 'dashboard',
  isPdf = false,
}) => {
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const showLegend = false;
  let maxValue = 0;

  useEffect(() => {
    if (data) {
      setRowCount(Math.ceil(data.length / 11));
      const newMonths = getMonthListByMax(getMonth(startDate * 1000) + 1);
      const newData = newMonths.map((month: string, idx: number) => {
        const daysInMonth = getEffectiveDaysInMonth(startDate, month);
        const monthData = data.filter((item: any) => item.month === month);
        const divider =
          monthData !== null && monthData.length > 0
            ? monthData[0].type === 'walk'
              ? 1
              : daysInMonth
            : 1;
        const newType =
          monthData !== null && monthData.length > 0
            ? monthData[0].type
            : 'read';
        const newMale =
          monthData !== null && monthData.length > 0
            ? monthData[0].maleCount
              ? monthData[0].maleValue / (monthData[0].maleCount * divider)
              : 0
            : 0;
        const newFemale =
          monthData !== null && monthData.length > 0
            ? monthData[0].femaleCount
              ? monthData[0].femaleValue / (monthData[0].femaleCount * divider)
              : 0
            : 0;
        const newTotal =
          monthData !== null && monthData.length > 0
            ? monthData[0].maleCount + monthData[0].femaleCount
              ? (monthData[0].maleValue + monthData[0].femaleValue) /
                ((monthData[0].maleCount + monthData[0].femaleCount) * divider)
              : 0
            : 0;
        maxValue = newTotal > maxValue ? newTotal : maxValue;
        return {
          month: month,
          전체: newType !== 'walk' ? newTotal.toFixed(2) : newTotal.toFixed(0),
          남자: newType !== 'walk' ? newMale.toFixed(2) : newMale.toFixed(0),
          여자:
            newType !== 'walk' ? newFemale.toFixed(2) : newFemale.toFixed(0),
        };
      });
      setRateData(newData);
    }
  }, [data]);

  return (
    <>
      <div className='basic_tab_content tab_content' ref={ref}>
        {data && data.length ? (
          <>
            <div
              className='bg_lightgray'
              style={{ width: width, height: '500px', backgroundColor: '#fff' }}
            >
              {!showLegend && <SwGenderLegend />}

              <ResponsiveContainer width='100%' height='100%'>
                <ComposedChart
                  width={500}
                  height={400}
                  data={rateData}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <defs>
                    <linearGradient id='color' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#9B66F9' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#C8C0F7' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='color2' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#2681FF' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#92BDFF' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='color3' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#FF4975' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#FFA88C' stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke='#f5f5f5' />
                  <XAxis
                    dataKey='month'
                    unit='월'
                    tickLine={false}
                    padding={{ left: 30, right: 30 }}
                  />
                  <YAxis
                    yAxisId='left'
                    unit=''
                    tickLine={false}
                    domain={[0, maxValue]}
                  />
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload, label }: any) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box className='graph_tooltip_box'>
                            <p className='graph_tooltip_title'>
                              {payload[0].payload.month}월
                            </p>
                            <div className='graph_tooltip_content_box'>
                              <p>남자</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['남자']}
                              </p>
                            </div>
                            <div className='graph_tooltip_content_box'>
                              <p>여자</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['여자']}
                              </p>
                            </div>
                          </Box>
                        );
                      }
                      return null;
                    }}
                    cursor={false}
                  />
                  {showLegend && (
                    <Legend verticalAlign='top' height={36} iconType='circle' />
                  )}
                  <Bar
                    yAxisId='left'
                    dataKey='남자'
                    barSize={10}
                    fill='url(#color)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='여자'
                    barSize={10}
                    fill='url(#color2)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='전체'
                    barSize={10}
                    fill='url(#color3)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{ paddingTop: '2rem' }}>
              <div className='table_style01'>
                {!isPdf && (
                  <div>
                    <table style={{ whiteSpace: 'nowrap' }}>
                      <thead>
                        <tr>
                          <th>월</th>
                          {rateData.map((d: any, idx: number) => (
                            <th key={idx.toString()}>{d.month}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>전체</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>
                              {commaFormat(d['전체'])}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>남자</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>
                              {commaFormat(d['남자'])}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>여자</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>
                              {commaFormat(d['여자'])}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {isPdf && (
                  <div>
                    {Array(rowCount)
                      .fill(0)
                      .map((_d, idx) => (
                        <table
                          key={idx.toString()}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <colgroup>
                            {Array(12)
                              .fill(0)
                              .map((d, subidx) => (
                                <col
                                  key={subidx.toString()}
                                  width={`${100 / 12}%`}
                                />
                              ))}
                          </colgroup>
                          <thead>
                            <tr>
                              <th>월</th>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <th key={subidx.toString()}>{d.month}</th>
                                ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>남자</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['남자'])}
                                  </td>
                                ))}
                            </tr>
                            <tr>
                              <td>여자</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['여자'])}
                                  </td>
                                ))}
                            </tr>
                          </tbody>
                        </table>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <Box sx={{ height: '500px' }}>
            <NoDataPage />
          </Box>
        )}
      </div>
    </>
  );
};
